import React, { useState } from 'react';
import { 
  TrendingUp, TrendingDown, BarChart3, LineChart, PieChart, 
  Calendar, Filter, Download, RefreshCw, Eye, Settings,
  Activity, Users, Pill, AlertTriangle, CheckCircle,
  Clock, Target, Award, Zap, Shield, Heart
} from 'lucide-react';

const TrendAnalysis = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [chartType, setChartType] = useState('line');
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Mock trend analysis data
  const trendData = {
    medicationAdherence: {
      title: 'Medication Adherence',
      current: 94.2,
      previous: 91.8,
      trend: '+2.4%',
      direction: 'up',
      data: [
        { date: '2024-06-01', value: 89.5, target: 95 },
        { date: '2024-06-08', value: 91.2, target: 95 },
        { date: '2024-06-15', value: 92.8, target: 95 },
        { date: '2024-06-22', value: 93.5, target: 95 },
        { date: '2024-06-29', value: 94.2, target: 95 },
        { date: '2024-07-06', value: 94.8, target: 95 },
        { date: '2024-07-13', value: 95.1, target: 95 },
        { date: '2024-07-20', value: 94.2, target: 95 }
      ]
    },
    qualityMetrics: {
      title: 'Quality Metrics',
      current: 96.8,
      previous: 94.5,
      trend: '+2.3%',
      direction: 'up',
      data: [
        { date: '2024-06-01', value: 92.1, target: 95 },
        { date: '2024-06-08', value: 93.8, target: 95 },
        { date: '2024-06-15', value: 94.2, target: 95 },
        { date: '2024-06-22', value: 95.1, target: 95 },
        { date: '2024-06-29', value: 95.8, target: 95 },
        { date: '2024-07-06', value: 96.2, target: 95 },
        { date: '2024-07-13', value: 96.5, target: 95 },
        { date: '2024-07-20', value: 96.8, target: 95 }
      ]
    },
    incidentRate: {
      title: 'Incident Rate',
      current: 2.1,
      previous: 3.4,
      trend: '-38.2%',
      direction: 'down',
      data: [
        { date: '2024-06-01', value: 4.2, target: 3.0 },
        { date: '2024-06-08', value: 3.8, target: 3.0 },
        { date: '2024-06-15', value: 3.5, target: 3.0 },
        { date: '2024-06-22', value: 3.1, target: 3.0 },
        { date: '2024-06-29', value: 2.8, target: 3.0 },
        { date: '2024-07-06', value: 2.5, target: 3.0 },
        { date: '2024-07-13', value: 2.3, target: 3.0 },
        { date: '2024-07-20', value: 2.1, target: 3.0 }
      ]
    },
    staffSatisfaction: {
      title: 'Staff Satisfaction',
      current: 87.5,
      previous: 84.2,
      trend: '+3.3%',
      direction: 'up',
      data: [
        { date: '2024-06-01', value: 82.1, target: 85 },
        { date: '2024-06-08', value: 83.5, target: 85 },
        { date: '2024-06-15', value: 84.2, target: 85 },
        { date: '2024-06-22', value: 85.1, target: 85 },
        { date: '2024-06-29', value: 85.8, target: 85 },
        { date: '2024-07-06', value: 86.2, target: 85 },
        { date: '2024-07-13', value: 86.9, target: 85 },
        { date: '2024-07-20', value: 87.5, target: 85 }
      ]
    },
    residentSatisfaction: {
      title: 'Resident Satisfaction',
      current: 91.3,
      previous: 89.7,
      trend: '+1.6%',
      direction: 'up',
      data: [
        { date: '2024-06-01', value: 88.2, target: 90 },
        { date: '2024-06-08', value: 88.9, target: 90 },
        { date: '2024-06-15', value: 89.3, target: 90 },
        { date: '2024-06-22', value: 89.7, target: 90 },
        { date: '2024-06-29', value: 90.1, target: 90 },
        { date: '2024-07-06', value: 90.5, target: 90 },
        { date: '2024-07-13', value: 90.9, target: 90 },
        { date: '2024-07-20', value: 91.3, target: 90 }
      ]
    },
    costEfficiency: {
      title: 'Cost Efficiency',
      current: 88.7,
      previous: 85.4,
      trend: '+3.3%',
      direction: 'up',
      data: [
        { date: '2024-06-01', value: 83.1, target: 85 },
        { date: '2024-06-08', value: 84.2, target: 85 },
        { date: '2024-06-15', value: 84.8, target: 85 },
        { date: '2024-06-22', value: 85.4, target: 85 },
        { date: '2024-06-29', value: 86.1, target: 85 },
        { date: '2024-07-06', value: 87.2, target: 85 },
        { date: '2024-07-13', value: 87.9, target: 85 },
        { date: '2024-07-20', value: 88.7, target: 85 }
      ]
    }
  };

  const categoryBreakdown = {
    'Medication Management': {
      adherence: 94.2,
      errors: 1.8,
      efficiency: 92.5,
      cost: 88.3
    },
    'Clinical Care': {
      assessments: 96.8,
      carePlans: 94.1,
      documentation: 98.2,
      incidents: 2.1
    },
    'Quality & Safety': {
      compliance: 97.5,
      audits: 95.8,
      training: 93.2,
      safety: 96.1
    },
    'Staff & Operations': {
      satisfaction: 87.5,
      retention: 89.2,
      productivity: 91.8,
      efficiency: 88.7
    }
  };

  const showNotification = (message, type) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const getTrendIcon = (direction) => {
    return direction === 'up' ? 
      <TrendingUp className="w-4 h-4 text-green-500" /> : 
      <TrendingDown className="w-4 h-4 text-red-500" />;
  };

  const getTrendColor = (direction) => {
    return direction === 'up' ? 'text-green-600' : 'text-red-600';
  };

  const handleExportData = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showNotification('Trend analysis data exported successfully', 'success');
    }, 1500);
  };

  const handleRefreshData = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showNotification('Trend data refreshed successfully', 'success');
    }, 1000);
  };

  const handleViewDetails = (metric) => {
    console.log('Viewing details for:', metric);
    showNotification(`Viewing detailed analysis for ${metric}`, 'info');
  };

  if (!onClose) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-[100]">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-4 mb-2 rounded-lg shadow-md text-white flex items-center gap-3 ${
                notification.type === 'success' ? 'bg-green-500' :
                notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
              }`}
            >
              {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {notification.type === 'error' && <AlertTriangle className="w-5 h-5" />}
              {notification.type === 'info' && <BarChart3 className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-indigo-600" />
            Trend Analysis & Historical Data
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex gap-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>
            
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Metrics</option>
              <option value="medication">Medication Adherence</option>
              <option value="quality">Quality Metrics</option>
              <option value="incidents">Incident Rate</option>
              <option value="staff">Staff Satisfaction</option>
              <option value="resident">Resident Satisfaction</option>
              <option value="cost">Cost Efficiency</option>
            </select>
            
            <select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="line">Line Chart</option>
              <option value="bar">Bar Chart</option>
              <option value="area">Area Chart</option>
            </select>
          </div>
          
          <div className="flex gap-2 ml-auto">
            <button
              onClick={handleRefreshData}
              disabled={loading}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
              } text-white`}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExportData}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {Object.entries(trendData).map(([key, metric]) => (
            <div key={key} className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {key === 'medicationAdherence' && <Pill className="w-5 h-5 text-blue-600" />}
                  {key === 'qualityMetrics' && <Award className="w-5 h-5 text-green-600" />}
                  {key === 'incidentRate' && <AlertTriangle className="w-5 h-5 text-red-600" />}
                  {key === 'staffSatisfaction' && <Users className="w-5 h-5 text-purple-600" />}
                  {key === 'residentSatisfaction' && <Heart className="w-5 h-5 text-pink-600" />}
                  {key === 'costEfficiency' && <Target className="w-5 h-5 text-orange-600" />}
                  <h3 className="font-semibold text-gray-800">{metric.title}</h3>
                </div>
                <button
                  onClick={() => handleViewDetails(metric.title)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-gray-900">
                    {typeof metric.current === 'number' && metric.current < 10 ? 
                      `${metric.current}%` : 
                      typeof metric.current === 'number' ? 
                      metric.current.toFixed(1) : 
                      metric.current
                    }
                  </span>
                  <div className={`flex items-center gap-1 ${getTrendColor(metric.direction)}`}>
                    {getTrendIcon(metric.direction)}
                    <span className="text-sm font-medium">{metric.trend}</span>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  Previous: {typeof metric.previous === 'number' ? 
                    (metric.previous < 10 ? `${metric.previous}%` : metric.previous.toFixed(1)) : 
                    metric.previous
                  }
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      metric.direction === 'up' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    style={{width: `${Math.min(100, Math.max(0, metric.current))}%`}}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Chart Visualization */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-8">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Historical Trend Analysis</h3>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Last {timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : timeRange === '90d' ? '90 days' : 'year'}</span>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="h-80 bg-gray-50 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-600 mb-2">Interactive Chart Visualization</h4>
                <p className="text-gray-500 mb-4">
                  {chartType === 'line' ? 'Line Chart' : chartType === 'bar' ? 'Bar Chart' : 'Area Chart'} showing {selectedMetric === 'all' ? 'all metrics' : selectedMetric} trends
                </p>
                <div className="flex justify-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded"></div>
                    <span>Current Period</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-400 rounded"></div>
                    <span>Target</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span>Previous Period</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {Object.entries(categoryBreakdown).map(([category, metrics]) => (
            <div key={category} className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">{category}</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {Object.entries(metrics).map(([metric, value]) => (
                    <div key={metric} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 capitalize">
                        {metric.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-indigo-500"
                            style={{width: `${Math.min(100, Math.max(0, value))}%`}}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                          {value}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Insights and Recommendations */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-6">
          <h3 className="text-lg font-semibold text-indigo-800 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-indigo-600" />
            AI-Powered Insights & Recommendations
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-indigo-700 mb-2">Key Insights</h4>
              <ul className="space-y-2 text-sm text-indigo-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Medication adherence improved by 2.4% this month
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Incident rate decreased by 38.2% over the past 30 days
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Staff satisfaction reached 87.5%, exceeding target
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  Cost efficiency needs attention in Q3
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-indigo-700 mb-2">Recommendations</h4>
              <ul className="space-y-2 text-sm text-indigo-600">
                <li className="flex items-start gap-2">
                  <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  Implement medication reminder system for 95%+ adherence
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  Continue current safety protocols to maintain low incident rate
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  Focus on cost optimization strategies for Q3
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  Maintain staff engagement initiatives
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendAnalysis;
